import React, { useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import formatCurrency from './format'

export const defaultConfig = {
  locale: 'en-US',
  formats: {
    number: {
      USD: {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    },
  },
}

const IntlCurrencyInput = ({
  component: InputComponent,
  value,
  defaultValue,
  config,
  currency,
  max,
  autoFocus,
  autoSelect,
  autoReset,
  onChange,
  onBlur,
  onFocus,
  onKeyPress,
  inputRef,
  ...otherProps
}) => {
  const localInputRef = useCallback(
    (node) => {
      const isActive = node === document.activeElement

      if (node && autoFocus && !isActive) {
        node.focus()
      }
    },
    [autoFocus]
  )

  const [maskedValue, setMaskedValue] = useState(null)

  const safeConfig = useMemo(
    () => () => {
      const {
        formats: {
          number: {
            [currency]: { maximumFractionDigits },
          },
        },
      } = config

      const finalConfig = {
        ...defaultConfig,
        ...config,
      }

      // eslint-disable-next-line standard/computed-property-even-spacing
      finalConfig.formats.number[currency].minimumFractionDigits =
        maximumFractionDigits

      return finalConfig
    },
    [defaultConfig, config]
  )

  const clean = (number) => {
    if (typeof number === 'number') {
      return number
    }

    return Number(number.toString().replace(/[^0-9-]/g, ''))
  }

  const normalizeValue = (number) => {
    const {
      formats: {
        number: {
          [currency]: { maximumFractionDigits },
        },
      },
    } = safeConfig()
    let safeNumber = number

    if (typeof number === 'string') {
      safeNumber = clean(number)

      if (safeNumber % 1 !== 0) {
        safeNumber = safeNumber.toFixed(maximumFractionDigits)
      }
    } else {
      safeNumber = Number.isInteger(number)
        ? Number(number) * 10 ** maximumFractionDigits
        : number.toFixed(maximumFractionDigits)
    }

    return clean(safeNumber) / 10 ** maximumFractionDigits
  }

  const calculateValues = (inputFieldValue) => {
    const val = normalizeValue(inputFieldValue)
    const maskedVal = formatCurrency(val, safeConfig(), currency)

    return [val, maskedVal]
  }

  const updateValues = (val) => {
    const [calculatedValue, calculatedMaskedValue] = calculateValues(val)

    if (!max || calculatedValue <= max) {
      setMaskedValue(calculatedMaskedValue)

      return [calculatedValue, calculatedMaskedValue]
    }
    return [normalizeValue(maskedValue), maskedValue]
  }

  const handleChange = (event) => {
    event.preventDefault()

    const [val, maskedVal] = updateValues(event.target.value)

    maskedVal && onChange(event, val, maskedVal)
  }

  const handleBlur = (event) => {
    if (event.target.value === '') return
    const [val, maskedVal] = updateValues(event.target.value)

    autoReset && calculateValues(0)
    val === 0 && setMaskedValue('')

    maskedVal && onBlur(event, val, maskedVal)
  }

  const handleFocus = (event) => {
    autoSelect && event.target.select()
    if (event.target.value === '') return

    const [val, maskedVal] = updateValues(event.target.value)
    val === 0 && setMaskedValue('')
    maskedVal && onFocus(event, val, maskedVal)
  }

  const handleKeyUp = (event) => onKeyPress(event, event.key, event.keyCode)

  useEffect(() => {
    const currentValue = value || defaultValue || 0
    const [, maskedVal] = calculateValues(currentValue)

    setMaskedValue(value === null ? value : maskedVal)
  }, [currency, value, defaultValue, config])
  return (
    <InputComponent
      {...otherProps}
      ref={inputRef || localInputRef}
      value={maskedValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyUp={handleKeyUp}
    />
  )
}

IntlCurrencyInput.propTypes = {
  defaultValue: PropTypes.number,
  value: PropTypes.number,
  max: PropTypes.number,
  component: PropTypes.node.isRequired,
  currency: PropTypes.string.isRequired,
  config: PropTypes.shape().isRequired,
  autoFocus: PropTypes.bool.isRequired,
  autoSelect: PropTypes.bool.isRequired,
  autoReset: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
}

IntlCurrencyInput.defaultProps = {
  component: 'input',
  currency: 'USD',
  value: null,
  max: 999999,
  defaultValue: null,
  config: defaultConfig,
  autoFocus: false,
  autoSelect: false,
  autoReset: false,
  inputRef: null,
  onChange: (f) => f,
  onBlur: (f) => f,
  onFocus: (f) => f,
  onKeyPress: (f) => f,
}

export default IntlCurrencyInput
